import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../../client-redux/reducers';
import { Language } from '../../../languages/languageHandler';
import { fetchEventList, IEvent } from '../../../../libraries/helpdesk/events/events';
import PaginationComponent from '../../../components/pagination';
import { Loader, Table } from '@mantine/core';
import HelpdeskFilterBar, { HelpdeskFilterBarFieldsData } from '../../components/helpdesk-filterbar';
import EditEventManagement from './edit-event-modal';
import Header from '../../../components/util/header';
import { useFitTableRows } from '../../../components/util/fit-table-rows-hook';
import { useStatusOperDialog, isZarphSuccessResponse } from '../../../context/status-oper-dialog';
import { IconEdit } from '@tabler/icons-react';
import './styles.scss';

export enum MODE {
  READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

const HelpdeskEventManagement: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const controller = useRef(new AbortController()).current;
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | undefined>(undefined);

  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [list, setList] = useState<IEvent[]>([]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [modalMode, setModalMode] = useState<MODE>(MODE.READ);
  const [filterBarData, setFilterBarData] = useState<HelpdeskFilterBarFieldsData>();

  const { handleApiError } = useStatusOperDialog();

  useEffect(() => {
    if (filterBarData && limit) {
      loadEventManagList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: HelpdeskFilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadEventManagList = () => {
    if (!filterBarData || !limit) return;

    setLoading(true);
    setList([]);
    setTotalRows(0);

    fetchEventList(controller, filterBarData?.eventCode)
      .then(response => {
        if (isZarphSuccessResponse(response, [9004])) {
          setList(response?.data?.events || []);
          setTotalRows(response?.data?.totalRows || 0);
        } else {
          console.error('Error response:', response);
          handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
          setLoading(false);
        }
      })
      .catch(err => {
        // unexpected errors go here
        handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleRowClick = (event: IEvent) => {
    setSelectedEvent(event);
    setModalMode(MODE.READ);
    setOpenDetails(true);
  };

  const handleEditClick = (event: IEvent) => {
    setSelectedEvent(event);
    setModalMode(MODE.EDIT);
    setOpenDetails(true);
  };

  const handleNewEventClick = () => {
    setSelectedEvent({
      act: {
        destDesisor: false,
        destDispacter: false,
        destDistributionList: false,
        destMonitor: false
      },
      actCounter: 0,
      description: '',
      eventCode: 0,
      eventType: 0
    });
    setModalMode(MODE.NEW);
    setOpenDetails(true);
  };

  return (
    <>
      <Header pageTitle={lang.EVENT_MANAGEMENT} />

      <HelpdeskFilterBar
        onSearch={loadEventManagList}
        onDataChange={handleFilterBarOnDataChange}
        fieldsUsage={{
          eventCode: true
        }}
        addButton={handleNewEventClick}
      />

      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{lang.EVENT_CODE}</Table.Th>
              <Table.Th>{lang.EQUIPMENT_DESCRIPTION}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            ) : !list.length ? (
              <Table.Tr>
                <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              list?.map((eventItem, index) => (
                <Table.Tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(eventItem)}>
                  <Table.Td>{eventItem.eventCode}</Table.Td>
                  <Table.Td>{eventItem.description}</Table.Td>
                  <Table.Td onClick={event => event.stopPropagation()}>
                    <IconEdit
                      stroke={2}
                      style={{ cursor: 'pointer' }}
                      onClick={event => {
                        event.stopPropagation();
                        handleEditClick(eventItem);
                      }}
                    />
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <PaginationComponent
        total={totalRows}
        itemsPerPage={limit}
        dynamicLimit={limit}
        currentPage={currentPage}
        onPageChange={changePage}
        onResultsChange={resultsSelect}
      />

      {selectedEvent && (
        <EditEventManagement
          mode={modalMode}
          isOpen={openDetails}
          hideBtn={() => setOpenDetails(false)}
          lang={lang}
          event={selectedEvent}
        />
      )}
    </>
  );
};

export default HelpdeskEventManagement;
